/**
 * @file AI 補助ページ — 資料に紐づく学習補助コンテンツを開く面。
 *
 * @what: /study/:familyId/sources/:sourceId/aids/:aidId。registry から補助ページを引き、
 *   本文を lazy に読み込んで出す。原本のリーダーへ戻る導線を上に置く。
 * @why: 取り込んだ本は原本のページ順でしか読めない。AI が原本から作った別の切り口
 *   (タイムライン・ゲーム別ガイド・さくいん) を、資料の付属ページとして持つ
 *   (代表 2026-08-27)。
 * @failure: 未知の資料 / 補助ページは 1 行で知らせ、本だなへ戻す。
 */

import { Suspense } from "react";
import { Link, useParams } from "react-router-dom";
import { findAid } from "@/features/study/aids/registry";
import { useLearnerSelection } from "@/features/study/hooks/useLearnerSelection";
import { useStudyBackground } from "@/features/study/hooks/useStudyBackground";
import "@/features/study/components/study.css";

export function StudyAidRoute() {
  const { familyId = "", sourceId = "", aidId = "" } = useParams();
  useStudyBackground();
  // 読みものはまなぶ人によらないが、だれを扱っているかはどの面でもヘッダーに出す。
  useLearnerSelection(familyId);
  const aid = findAid(sourceId, aidId);
  const sourceHref = `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(sourceId)}`;

  if (!aid) {
    return (
      <div className="study-scope study-page">
        <p role="alert" className="study-error">
          このページは見つかりませんでした。
        </p>
        <Link to={`/study/${encodeURIComponent(familyId)}`}>本だなへ戻る</Link>
      </div>
    );
  }

  const { Component } = aid;
  return (
    <div className="study-scope study-page">
      <p className="study-aid-back">
        <Link to={sourceHref}>← 本のページに戻る</Link>
      </p>
      <h1 className="study-aid-title">{aid.title}</h1>
      <p className="study-aid-tagline">{aid.tagline}</p>
      <Suspense fallback={<p className="study-muted">読み込んでいます...</p>}>
        <Component familyId={familyId} sourceHref={sourceHref} />
      </Suspense>
    </div>
  );
}
